import React, {
  useState,
  useRef,
  ChangeEvent,
  ChangeEventHandler,
  useEffect,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import map from 'lodash/map';
import styles from './SearchDropdown.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import useClickOutside from '../../CustomHooks/useClickOutside';
import Icon from '../Icon/Icon';

type DropdownItem = {
  code: number | string;
  value: string;
  status?: number;
  fromDate?: string;
  toDate?: string;
  localName?: string;
  accountManagerNativeId?: string;
  affiliateCode?: string;
  marketCountryCode?: string;
  marketName?: string;
};

interface SearchDropdownProps {
  dropdownName: string;
  items: DropdownItem[] | undefined;
  selectedItem: DropdownItem | null;
  handleSelectItemCallback: (docType: DropdownItem | null) => void;
  isLoading?: boolean;
  size?: 'sm' | 'md';
  hideDropdownIcon?: boolean;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  dropdownName,
  items,
  selectedItem,
  handleSelectItemCallback,
  isLoading = false,
  size = 'md',
  hideDropdownIcon = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;
  let arr: number[] = new Array(5).fill(0);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);

  const toggleDropdown = () => {
    setIsOpen((o) => !o);
  };

  const handleSelectItem = (item: DropdownItem) => {
    handleSelectItemCallback(item);
    setSearch(item.value);
    setIsOpen(false);
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    setSearch(value);
    setFilteredItems(filterItemsBySearch(value));
  };

  const filterItemsBySearch = useCallback(
    (searchValue: string) => {
      if (!items) return [];
      return items.filter((item) => item.value.toLowerCase().includes(searchValue.toLowerCase()));
    },
    [items]
  );

  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  const handleClear = () => {
    handleSelectItemCallback(null);
    setSearch('');
    setFilteredItems(items);
  };

  const getIconColor = (disabled?: boolean) => {
    if (disabled) return currentTheme === 'light' ? '#5E5E5E' : '#8E8E93';
    return currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9';
  };

  const renderItems = () => {
    if (isLoading)
      return arr.map((_, index) => (
        <div key={index} className={`${styles.dropdown_item} ${styles.loader} animate`}></div>
      ));

    if (!items?.length) {
      return (
        <div className={`${styles.dropdown_item} ${styles.message}`}>
          {t('dropdown_items_not_available')}
        </div>
      );
    }
    if (!filteredItems?.length) {
      return (
        <div className={`${styles.dropdown_item} ${styles.message}`}>
          {t('dropdown_search_not_found')}
        </div>
      );
    }

    return map(filteredItems, (item, index) => (
      <div
        key={item?.code ?? index}
        className={`dropdown_item ${styles.dropdown_item} ${selectedItem?.code === item?.code ? `selected ${styles.selected}` : ''}`}
        onClick={() => handleSelectItem(item)}
      >
        {item ? `${item?.value} - ${item.code}` : ''}
      </div>
    ));
  };

  return (
    <div ref={dropdownRef} className={`dropdown ${styles.dropdown}`}>
      <div
        className={`${styles.input_wrapper} ${styles[`size_${size}`]}`}
        data-testid="toggle_drodpwn_testId"
      >
        <input
          type="text"
          className={`dropdown_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''} ${styles[`size_${size}`]}`}
          value={search}
          onChange={handleSearch}
          data-testid="dropdown_input_testId"
          placeholder={dropdownName}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            selectedItem && setSearch(selectedItem.value);
          }}
        />
        <Icon
          name="search"
          size={size === 'sm' ? 20 : 24}
          color={currentTheme === 'light' ? '#2c2c2c' : '#AEAEB2'}
          className={styles.search_icon}
        />
        {selectedItem && (
          <button className={`clear_btn ${styles.clear_btn}`} onClick={handleClear}>
            <Icon name="cross_icon" color={getIconColor()} size={12} />
          </button>
        )}
        {!hideDropdownIcon && (
          <img
            src={dropdownArrow}
            alt="Downkey"
            className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
            onClick={toggleDropdown}
          />
        )}
      </div>

      {isOpen && (
        <div
          className={`dropdown_menu ${styles.dropdown_menu} ${styles.dropdown_height} ${isLoading ? styles.dropdown_loader : ''}`}
          data-testid="drodpwn_list_testId"
        >
          {renderItems()}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
